import { Component } from 'react'

/**
 * The MECHANISM only. It carries no copy and renders no words of its own.
 *
 * There are two boundaries in this app and they must say different things:
 * BatteryBoundary may state that her answers are saved, because on an answering
 * surface that is true and it is the only fact she needs. RootBoundary may not,
 * because outside the battery there may be no answers in flight at all and the
 * sentence would be a claim about data that does not exist.
 *
 * Decision #82 rejected that CLAIM being made everywhere, not the boundary
 * being everywhere. So the split is enforced structurally: this class takes a
 * `fallback` node and has no default, no copy constant and no strings. There is
 * nothing here for a second boundary to inherit by accident, and adding copy to
 * this file would be the mistake #82 exists to prevent.
 *
 * A class because this is the one thing React still has no hook for. It is not
 * a pattern for anything else in this app.
 */
export class ErrorBoundary extends Component {
  state = { crashed: false }

  static getDerivedStateFromError() {
    // The error is deliberately NOT held in state. Nothing renders it, so
    // keeping it would only invite someone to.
    return { crashed: true }
  }

  componentDidCatch(error, info) {
    // For whoever reads the console, never for the participant. There is no
    // client error-reporting endpoint in v0 and this is not the ticket that
    // invents one.
    console.error(`[${this.props.label ?? 'app'}] render crashed`, error, info?.componentStack)
  }

  render() {
    return this.state.crashed ? this.props.fallback : this.props.children
  }
}

/**
 * Focus the crash heading on mount so a screen reader announces the one thing
 * that changed — everything visible was replaced, and nothing else would say
 * so. Not role="alert": the screen has replaced everything and an alert
 * competes with the heading.
 *
 * tabIndex -1 makes it programmatically focusable without entering the tab
 * order; the outline is suppressed because focus here is not a navigation state
 * she chose, and a ring on a heading reads as a control.
 *
 * Shared because it is behaviour, not words. The heading text is passed in.
 */
export function CrashHeading({ children, headingRef }) {
  return (
    <h1
      ref={headingRef}
      tabIndex={-1}
      className="t-screen mb-[var(--s-5)] text-[var(--ink-900)] focus:outline-none"
    >
      {children}
    </h1>
  )
}
